import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Divider,
  Header,
  Icon,
  Loader,
  Message,
} from "semantic-ui-react";

import { i18n } from "simplydo/core/i18n";
import type { OpenAPI } from "simplydo/interfaces";

import api from "api";
import { useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";

import { UserChip } from "components/lib/Chips";

const getEntityAction = (
  persistentToken: OpenAPI.Schemas["PersistentToken"],
) => {
  const { forEntity } = persistentToken;
  if (forEntity === "invitation") {
    return "invited";
  }
  return "";
};

const getForTypeText = (
  persistentToken: OpenAPI.Schemas["PersistentToken"],
) => {
  const { forType } = persistentToken;
  if (forType === "ideaBusinessProfile") {
    return `join the business profile`;
  } else if (forType === "idea") {
    return `join the ${i18n.t("generic.idea")}`;
  } else if (forType === "group") {
    return `join the ${i18n.t("generic.group")}`;
  }
  return "";
};

const getEntityName = (persistentToken: OpenAPI.Schemas["PersistentToken"]) => {
  const { forType } = persistentToken;
  if (forType === "ideaBusinessProfile") {
    const { ownerIdeaBusinessProfile } = persistentToken;
    return ownerIdeaBusinessProfile?.name ?? "Unknown";
  } else if (forType === "idea") {
    const { ownerIdea } = persistentToken;
    return ownerIdea?.name ?? "Unknown";
  } else if (forType === "group") {
    const { ownerGroup } = persistentToken;
    return ownerGroup?.name ?? "Unknown";
  }
  return "";
};

const getActionText = (persistentToken: OpenAPI.Schemas["PersistentToken"]) => {
  const { forEntity } = persistentToken;
  if (forEntity === "invitation") {
    return "accept this invitation";
  }
  return "";
};

const PersistentToken = () => {
  const reloadUser = useUserReload();
  const [persistentToken, setPersistentToken] = useState<
    | OpenAPI.GET<"/persistent-tokens/redeem/{token}">["response"]["persistentToken"]
    | null
  >(null);
  const [persistentTokenLoading, setPersistentTokenLoading] = useState(false);
  const [persistentTokenErrorMessage, setPersistentTokenErrorMessage] =
    useState<string>("");

  const [redeemingPersistentToken, setRedeemingPersistentToken] =
    useState(false);

  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  const { token } = useParams<{ token: string }>();

  // If we have data indicating the user has previously visited this link, and created an entity/accepted an invitation, redirect them to the entity
  const handleRedirect = useCallback(
    (
      persistentToken: OpenAPI.GET<"/persistent-tokens/redeem/{token}">["response"]["persistentToken"],
    ) => {
      if (persistentToken.forEntity === "invitation") {
        if (persistentToken.forType === "ideaBusinessProfile") {
          navigate(`/?businessProfileId=${persistentToken.forId}`); // See AutoBusinessProfileModal.tsx
        } else if (persistentToken.forType === "idea") {
          navigate(`/ideas/${persistentToken.forId}`);
        } else if (persistentToken.forType === "group") {
          navigate(`/groups/${persistentToken.forId}`);
        }
      }
    },
    [navigate],
  );

  const getPersistentToken = useCallback(() => {
    if (!token) return;

    setPersistentTokenLoading(true);
    setPersistentTokenErrorMessage("");

    api.persistentTokens.getByToken(
      token,
      (res: OpenAPI.GET<"/persistent-tokens/redeem/{token}">["response"]) => {
        if (res.userHasRedeemedToken) {
          handleRedirect(res.persistentToken);
        }
        setPersistentToken(res.persistentToken);
        setPersistentTokenLoading(false);
      },
      (err) => {
        setPersistentTokenErrorMessage(err.message);
        setPersistentTokenLoading(false);
      },
    );
  }, [handleRedirect, token]);

  useEffect(() => {
    getPersistentToken();
  }, [getPersistentToken]);

  const redeemPersistentToken = useCallback(() => {
    if (!token || !persistentToken) return;

    setRedeemingPersistentToken(true);
    setPersistentTokenErrorMessage("");
    api.persistentTokens.redeem(
      token,
      () => {
        toast.success("Invitation accepted");
        reloadUser(() => {
          handleRedirect(persistentToken);
        });
        setRedeemingPersistentToken(false);
      },
      (err) => {
        setRedeemingPersistentToken(false);
        setPersistentTokenErrorMessage(err.message);
      },
    );
  }, [persistentToken, reloadUser, token, handleRedirect]);

  return (
    <div style={{ paddingBottom: 5 }}>
      {persistentTokenErrorMessage && (
        <>
          <h1>Invitations</h1>
          <Divider />
        </>
      )}

      {persistentToken ?
        <>
          <Header as="h2" icon textAlign="center">
            <Icon name="users" circular />
            <Header.Content>
              Join {`"${getEntityName(persistentToken)}"`}
            </Header.Content>
          </Header>
          <p>
            {persistentToken.ownerUser ?
              <UserChip user={persistentToken.ownerUser} compact="very" />
            : "Someone"}
            {" has "}
            {getEntityAction(persistentToken)}
            {" you to "}
            {getForTypeText(persistentToken)}
          </p>
        </>
      : null}

      {persistentTokenErrorMessage ?
        <>
          <Message error>
            <Message.Content>
              {persistentTokenErrorMessage}. Your link may be invalid, or has
              expired. If you think this is a mistake or if you need help,
              please contact the person who sent you the invitation.
            </Message.Content>
          </Message>
          <Divider />
          <p>You can still log in to view your account or sign up below.</p>
          <Button primary as={Link} to={`/login`} style={{ marginBottom: 5 }}>
            Log in or sign up
          </Button>
        </>
      : null}

      {persistentTokenLoading ?
        <Loader active inline="centered" />
      : null}

      {persistentToken ?
        user ?
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 35,
              marginTop: 20,
              width: "100%",
            }}
          >
            <Button
              primary
              fluid
              icon="check"
              loading={redeemingPersistentToken}
              onClick={redeemPersistentToken}
              style={{ maxWidth: 500 }}
              disabled={!!persistentTokenErrorMessage}
            >
              Accept invitation
            </Button>
          </div>
        : <>
            <Message style={{ marginTop: 25 }} info>
              To {getActionText(persistentToken)}, please log in or sign up.
            </Message>
            <Button
              style={{ float: "right" }}
              as={Link}
              content="Log in or sign up"
              primary
              to={`/login?then=/join/${token}`}
            />
          </>

      : null}
    </div>
  );
};

export default PersistentToken;
